/** Inbound demo requests captured by the landing-page "Book a demo" modal. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { Card } from '../components/SharedComponents';
import { API_BASE } from '../config';
import colors from '../utils/adminColors';

const STATUS_META = {
  new: { label: 'New', color: colors.success },
  contacted: { label: 'Contacted', color: colors.textSecondary },
  archived: { label: 'Archived', color: colors.textMuted },
  honeypot_rejected: { label: 'Spam', color: colors.danger },
};

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status, color: colors.textSecondary };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '999px',
      fontSize: '12px', fontWeight: 600, color: meta.color,
      border: `1px solid ${meta.color}`, whiteSpace: 'nowrap',
    }}>{meta.label}</span>
  );
}

function RequestRow({ item, onSetStatus, busy }) {
  const mailto = `mailto:${item.email}?subject=${encodeURIComponent('Wingman demo')}`;
  return (
    <div style={{
      padding: '18px 0', borderTop: `1px solid ${colors.borderLight}`,
      display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: '16px',
      alignItems: 'start',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ color: colors.darkText, fontSize: '15px', fontWeight: 600 }}>
            {item.name}
          </span>
          <StatusPill status={item.status} />
          {!item.email_sent && item.status !== 'honeypot_rejected' && (
            <span
              title="The lead was stored but the notification email did not send."
              style={{ color: colors.warning, fontSize: '12px', fontWeight: 600 }}
            >not emailed</span>
          )}
        </div>

        <div style={{ marginTop: '4px', fontSize: '13px', color: colors.textSecondary }}>
          <a href={mailto} style={{ color: colors.accent, textDecoration: 'none' }}>{item.email}</a>
          {item.company ? <span> · {item.company}</span> : null}
        </div>

        {item.note ? (
          <div style={{
            marginTop: '10px', padding: '10px 12px', borderRadius: '8px',
            backgroundColor: colors.lightGray, color: colors.darkText,
            fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap',
          }}>{item.note}</div>
        ) : null}

        <div style={{ marginTop: '8px', fontSize: '12px', color: colors.textMuted }}>
          {formatWhen(item.created_at)}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {item.status !== 'contacted' && (
          <button
            type="button" disabled={busy}
            onClick={() => onSetStatus(item.demo_request_id, 'contacted')}
            style={{
              padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
              border: `1px solid ${colors.accent}`, backgroundColor: colors.accent,
              color: '#fff', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
            }}
          >Mark contacted</button>
        )}
        {item.status !== 'archived' && (
          <button
            type="button" disabled={busy}
            onClick={() => onSetStatus(item.demo_request_id, 'archived')}
            style={{
              padding: '7px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
              border: `1px solid ${colors.border}`, backgroundColor: 'transparent',
              color: colors.textSecondary, cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >Archive</button>
        )}
      </div>
    </div>
  );
}

export default function AdminDemoRequests() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [includeSpam, setIncludeSpam] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const requestSequence = useRef(0);

  const fetchPage = useCallback(async (cursor) => {
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (cursor) params.set('cursor', String(cursor));
      if (includeSpam) params.set('include_spam', 'true');
      const response = await fetch(`${API_BASE}/api/admin/demo-requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${response.status}`);
      }
      const data = await response.json();
      if (requestSequence.current !== requestId) return;
      setItems((prev) => (cursor ? [...prev, ...(data.items || [])] : (data.items || [])));
      setNextCursor(data.next_cursor || null);
    } catch (fetchError) {
      if (requestSequence.current === requestId) {
        setError(fetchError.message || 'Failed to load demo requests');
      }
    } finally {
      if (requestSequence.current === requestId) setLoading(false);
    }
  }, [token, includeSpam]);

  useEffect(() => { fetchPage(null); }, [fetchPage]);

  const setStatus = useCallback(async (id, status) => {
    setBusyId(id);
    // Optimistic, then reconciled from the server's echo below. On failure we
    // restore the previous value rather than leaving the row lying about itself.
    const previous = items.find((i) => i.demo_request_id === id)?.status;
    setItems((prev) => prev.map((i) => (
      i.demo_request_id === id ? { ...i, status } : i
    )));
    try {
      const response = await fetch(`${API_BASE}/api/admin/demo-requests/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = await response.json();
      setItems((prev) => prev.map((i) => (
        i.demo_request_id === id ? { ...i, status: body.status } : i
      )));
    } catch (updateError) {
      setItems((prev) => prev.map((i) => (
        i.demo_request_id === id ? { ...i, status: previous } : i
      )));
      setError('That change did not save. Please try again.');
    } finally {
      setBusyId(null);
    }
  }, [token, items]);

  const newCount = items.filter((i) => i.status === 'new').length;

  return (
    <div style={{ padding: '32px', backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ color: colors.darkText, fontSize: 'var(--font-size-hero)', marginBottom: '8px' }}>
          Demo Requests
        </h1>
        <p style={{ color: colors.textMuted, fontSize: 'var(--font-size-base)', margin: 0 }}>
          People who asked for a demo from the landing page. Newest first.
        </p>
      </div>

      <Card>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '16px', flexWrap: 'wrap', paddingBottom: '14px',
        }}>
          <div style={{ color: colors.darkText, fontSize: '14px', fontWeight: 600 }}>
            {loading && items.length === 0
              ? 'Loading...'
              : `${items.length} shown${newCount ? ` · ${newCount} new` : ''}`}
          </div>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            color: colors.textSecondary, fontSize: '13px', cursor: 'pointer',
          }}>
            <input
              type="checkbox" checked={includeSpam}
              onChange={(e) => setIncludeSpam(e.target.checked)}
            />
            Show spam
          </label>
        </div>

        {error ? (
          <div style={{
            padding: '12px 14px', borderRadius: '8px', marginBottom: '12px',
            backgroundColor: colors.dangerLight || 'rgba(229,84,75,0.08)',
            color: colors.danger, fontSize: '13px',
          }}>{error}</div>
        ) : null}

        {!loading && items.length === 0 && !error ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: colors.textMuted, fontSize: '14px' }}>
            No demo requests yet. They will appear here as soon as someone fills in the form.
          </div>
        ) : null}

        {items.map((item) => (
          <RequestRow
            key={item.demo_request_id}
            item={item}
            onSetStatus={setStatus}
            busy={busyId === item.demo_request_id}
          />
        ))}

        {nextCursor ? (
          <div style={{ paddingTop: '18px', textAlign: 'center' }}>
            <button
              type="button" disabled={loading}
              onClick={() => fetchPage(nextCursor)}
              style={{
                padding: '9px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                border: `1px solid ${colors.border}`, backgroundColor: 'transparent',
                color: colors.textSecondary, cursor: loading ? 'default' : 'pointer',
              }}
            >{loading ? 'Loading...' : 'Load more'}</button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
